import * as React from 'react';
import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { path, pathOr } from 'ramda';
import { FormInputsLayout } from '../../components/FormInputsLayout';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { isEmptyString, isValidNumber } from '../../utils/validators';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { numberValidator } from './utils/numberValidator';
import { useFormTracking } from '../../utils/useFormTracking';
import { removeLeadingZero } from '../../utils/formaters';
import { InputAdornment } from '@cian/ui-kit';

export const DEFAULT_NUMBER_PLACEHOLDER = 'Введите значение';

export const NumberQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, placeholder, title, required, unit },
    answersState,
    setNextStep,
    setAnswersState,
  } = props;

  const { trackSubmit, trackChange } = useFormTracking();

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm({
    onSubmit(values) {
      setAnswersState(prevState => {
        const value = values[answerField] ? Number(values[answerField]) : null;
        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value,
          },
        };

        setNextStep(nextAnswersState);
        trackSubmit({
          isRequired: required,
          field: answerField,
          value: `${value}`,
        });

        return nextAnswersState;
      });
    },
    validators: {
      [answerField]: numberValidator(required),
    },
    initialValues: pickOnlyMeaning({
      [answerField]: path([answerField, 'value'], answersState),
    }),
    required,
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      const value = e.target.value;

      if (isValidNumber(value) || isEmptyString(value)) {
        onChange(answerField, removeLeadingZero(e.target.value));
      }
    },
    [onChange, answerField, trackChange],
  );

  return (
    <QuestionLayoutContainer title={title} onSubmit={handleSubmit} disableSubmit={isSubmitDisabled}>
      <FormInputsLayout
        InputProps={{
          type: 'text',
          name: answerField,
          invalid: !!errors[answerField],
          errorMessage: errors[answerField],
          placeholder: placeholder || DEFAULT_NUMBER_PLACEHOLDER,
          onChange: handleChange,
          value: pathOr('', [answerField], values),
          rightAdornment: <InputAdornment>{unit}</InputAdornment>,
        }}
      />
    </QuestionLayoutContainer>
  );
};

NumberQuestionContainer.displayName = 'NumberQuestionContainer';
